<script lang="ts">
	import { onDestroy, type Snippet } from 'svelte';
	import { browser } from '$app/environment';
	import './layout.css';
	import favicon from '$lib/assets/favicon.svg';
	import * as Sidebar from '$lib/components/ui/sidebar/index.js';
	import { Toaster, toast } from 'svelte-sonner';
	import SquarePenIcon from '@lucide/svelte/icons/square-pen';
	import SearchIcon from '@lucide/svelte/icons/search';
	import BlocksIcon from '@lucide/svelte/icons/blocks';
	import ChartColumnIcon from '@lucide/svelte/icons/chart-column';
	import ClockIcon from '@lucide/svelte/icons/clock';
	import ShieldIcon from '@lucide/svelte/icons/shield';
	import FlaskConicalIcon from '@lucide/svelte/icons/flask-conical';
	import SessionList from '$lib/components/SessionList.svelte';
	import FolderList from '$lib/components/FolderList.svelte';
	import BulkActionBar from '$lib/components/BulkActionBar.svelte';
	import SearchDialog from '$lib/components/SearchDialog.svelte';
	import SharedWithMeList from '$lib/components/SharedWithMeList.svelte';
	import TrashList from '$lib/components/TrashList.svelte';
	import UpdateBanner from '$lib/components/UpdateBanner.svelte';
	import InstallPrompt from '$lib/components/InstallPrompt.svelte';
	import SidebarMobileOpener from '$lib/components/SidebarMobileOpener.svelte';
	import SidebarToggle from '$lib/components/SidebarToggle.svelte';
	import SidebarUserMenu from '$lib/components/SidebarUserMenu.svelte';
	import SidebarUsage from '$lib/components/SidebarUsage.svelte';
	import { edgeSwipe } from '$lib/actions/swipe';
	import { afterNavigate, beforeNavigate, goto, onNavigate } from '$app/navigation';
	import { page, updated } from '$app/state';
	import { refreshSessions } from '$lib/sessions-store.svelte';
	import { refreshFolders } from '$lib/folders-store.svelte';
	import { loadTempChats, sweepTemporaryChats } from '$lib/temp-chats.svelte';
	import { loadTabs, openTab } from '$lib/chat-tabs.svelte';
	import { pollScheduledBadge, scheduledHasUnread } from '$lib/scheduled-store.svelte';
	import { refreshSharedSessions } from '$lib/shared-sessions-store.svelte';
	import { registerSW } from '$lib/sw-update.svelte';
	import { resetOnboarding } from '$lib/marketplace-api';
	import {
		initAuth,
		login,
		logout,
		isAuthenticated,
		isDevBypass,
		authReady,
		profile,
		me,
		meReady,
		meErrored,
		reloadMe,
		setMe,
	} from '$lib/auth.svelte';
	import { decideGate } from '$lib/layout-gate';
	import { capturePageview, captureException, identifyUser, resetUser } from '$lib/posthog';
	import OnboardingWizard from '$lib/components/onboarding/OnboardingWizard.svelte';
	import { initReducedMotion, reducedMotion } from '$lib/reduced-motion';

	let { children }: { children: Snippet } = $props();

	// Browser-local chat organisation (SAY-1820): the open tabs and the
	// temporary-chat flags live in localStorage and must be in memory BEFORE the
	// first afterNavigate (which adds the routed chat as a tab) — loading them
	// later would overwrite the stored tab list with just the current chat.
	// Folders come from the manager and load once signed in (effect below).
	if (browser) {
		loadTabs();
		loadTempChats();
	}

	// One combined OS + in-app preference owns motion policy for the whole app.
	// JS-driven transitions consume the store; CSS transitions consume the root
	// data attribute installed by the same helper.
	const destroyReducedMotion = initReducedMotion();
	onDestroy(destroyReducedMotion);

	// Single source of truth for which top-level screen renders. A FAILED /me
	// routes to 'reconnect', NEVER the onboarding wizard (layout-gate.ts) — that
	// was "onboarding restarts each redeploy".
	const gate = $derived(
		decideGate({
			authReady: authReady(),
			isAuthenticated: isAuthenticated(),
			meReady: meReady(),
			meError: meErrored(),
			onboarded: me()?.onboarded ?? null,
		}),
	);

	// Auto-retry /me while stuck on the reconnect screen so a redeploy self-heals
	// without the user clicking Retry.
	$effect(() => {
		if (gate !== 'reconnect') return;
		const t = setTimeout(() => void reloadMe(), 5000);
		return () => clearTimeout(t);
	});

	// Admin-only (settings): reset onboarding so the first-run wizard runs again.
	// setMe flips onboarded=false → the layout gate below re-mounts <OnboardingWizard/>.
	async function handleResetOnboarding(): Promise<void> {
		try {
			const next = await resetOnboarding();
			setMe(next);
			toast.success('Onboarding reset — running the welcome wizard.');
		} catch (err) {
			toast.error('Reset failed', { description: err instanceof Error ? err.message : String(err) });
		}
	}

	// Auth0 login gate (auth.md "Login gate"). Run initAuth() once on mount: it
	// completes any redirect callback then loads the auth state into the reactive
	// store. Until that resolves we show nothing chrome-y; then either the
	// centered sign-in screen (unauthenticated) or the full app (authenticated).
	let signingIn = $state(false);
	$effect(() => {
		initAuth()
			.then(() => {
				// PostHog identity follows the verified Auth0 profile. The
				// SSoT for "who is this session" is the JWT `sub` exposed via
				// profile() (the manager uses the same sub as user_id). If
				// authentication didn't land a profile (signed out / dev-
				// bypass off), fall through unidentified — the SDK records
				// against an anonymous id and merges on a later identify.
				const p = profile();
				if (p?.sub) {
					identifyUser(p.sub, {
						email: p.email ?? null,
						name: p.name ?? null,
						onboarded: me()?.onboarded ?? null,
						is_admin: me()?.isAdmin ?? null,
						dev_bypass: isDevBypass(),
					});
				}
			})
			.catch((err) => {
				console.warn('[layout] initAuth failed', err);
				// The login funnel is otherwise uninstrumented — a broken Auth0
				// round-trip (clock skew, reused code, JWKS outage) would vanish
				// into the console. Capture it before resetting identity.
				captureException(err, { source: 'initAuth' });
				// Auth failed — drop any prior PostHog identity so the next
				// user on the same browser doesn't inherit it.
				resetUser();
			});
	});

	// Manual pageview emit. PostHog's built-in history-API listener is off
	// (capture_pageview: false in posthog.ts) so SvelteKit's client-side
	// router transitions count exactly once each. Registered at top level —
	// afterNavigate only attaches when called during component init.
	afterNavigate(() => {
		capturePageview();
		// Every chat you visit becomes a tab (SAY-1820); the sidebar keeps the
		// full history, the tab strip is the working set.
		const sid = page.params.sessionId;
		if (sid && page.url.pathname.startsWith('/s/')) openTab(sid);
	});

	async function handleSignIn(): Promise<void> {
		signingIn = true;
		try {
			await login();
		} catch {
			// loginWithRedirect navigates away on success; an error here means we
			// never left — re-enable the button so the user can retry.
			signingIn = false;
		}
	}

	// The label for the signed-in user in the sidebar footer (name > email > sub).
	const userLabel = $derived.by(() => {
		const p = profile();
		return p ? (p.name ?? p.email ?? p.sub) : '';
	});

	const activeId = $derived(page.params.sessionId);

	// The "Search" action (and ⌘K) opens the chat-search dialog: full-text
	// search over titles + messages, with a preview pane (SearchDialog.svelte).
	let searchOpen = $state(false);
	function toggleSearch(): void {
		searchOpen = !searchOpen;
	}

	// Keyboard shortcuts (Open WebUI `lib/shortcuts.ts`): ⌘K / Ctrl+K opens the
	// chat search, ⌘⇧O / Ctrl+Shift+O starts a new chat. ⌘B toggles the sidebar
	// (shadcn's own listener). Skipped while an editable element has focus for
	// plain letters; the modifier combos are safe everywhere.
	const isMac = $derived(
		typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? ''),
	);
	const modKey = $derived(isMac ? '⌘' : 'Ctrl+');
	$effect(() => {
		if (typeof window === 'undefined') return;
		const onKey = (e: KeyboardEvent): void => {
			const mod = e.metaKey || e.ctrlKey;
			if (!mod) return;
			if (e.key.toLowerCase() === 'k' && !e.shiftKey && !e.altKey) {
				e.preventDefault();
				toggleSearch();
			} else if (e.key.toLowerCase() === 'o' && e.shiftKey && !e.altKey) {
				e.preventDefault();
				void goto('/');
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	});

	$effect(() => {
		registerSW();
		// Only pull sessions once signed in AND onboarded — refreshSessions()
		// awaits an Auth0 token (throws before login), and the sidebar isn't
		// visible while the wizard is up so we'd be fetching for nothing. The
		// "Shared with me" list refreshes alongside on the same gate.
		if (isAuthenticated() && me()?.onboarded === true) {
			// Browser-local chat organisation (SAY-1820): tabs + temporary-chat
			// flags come from localStorage; folders from the manager. A temporary
			// chat left open when the browser died is deleted now (sweep) — except
			// the one being opened by this very navigation.
			void refreshFolders();
			void refreshSessions().then(() => sweepTemporaryChats(page.params.sessionId ?? null));
			void refreshSharedSessions();
		}
	});

	// Live sidebar + "Scheduled" unread dot. A timer-fired scheduled run creates its
	// chat server-side with no browser involved, so the sidebar wouldn't otherwise know.
	// While the tab is visible, poll to surface new run-chats (refreshSessions merges,
	// preserving running/rename state) and refresh the Scheduled badge. Paused when the
	// tab is hidden; runs immediately on becoming visible; cleared on unmount.
	$effect(() => {
		if (!(isAuthenticated() && me()?.onboarded === true)) return;
		const tick = () => {
			if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
			void refreshSessions();
			void pollScheduledBadge();
		};
		void pollScheduledBadge(); // prime the badge on mount
		const timer = setInterval(tick, 45_000);
		const onVisible = () => {
			if (document.visibilityState === 'visible') tick();
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			clearInterval(timer);
			document.removeEventListener('visibilitychange', onVisible);
		};
	});

	// SvelteKit polls `_app/version.json` (60s, configured in svelte.config.js)
	// and flips `updated.current` when a new build is detected. Mirror sayano-erp:
	// once a new version is known, force a full-page navigation on the next
	// route change so the user lands on fresh HTML with current chunk hashes —
	// otherwise the SPA keeps using the old code-split bundles and dynamic
	// imports start 404'ing as the deployed assets get garbage-collected.
	beforeNavigate(({ to, cancel }) => {
		if (updated.current && to?.url) {
			cancel();
			window.location.href = to.url.href;
		}
	});

	// Cross-fade route transitions via the View Transitions API. Falls back to
	// instant navigation in browsers without document.startViewTransition (e.g.
	// Firefox at the time of writing). Cross-fade rule lives in the scoped
	// style block below — Foundations agent owns layout.css, so we keep the
	// animation here as a :global() selector to avoid colliding.
	onNavigate((navigation) => {
		if (typeof document === 'undefined') return;
		if ($reducedMotion) return;
		if (!document.startViewTransition) return;
		return new Promise((resolve) => {
			document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});

	// Edge-swipe-from-left to open the mobile sidebar drawer. Only wired on
	// <md screens. Dispatches a window CustomEvent so SidebarMobileOpener
	// (which lives inside Sidebar.Provider, where useSidebar() works) can
	// flip the openMobile flag.
	$effect(() => {
		if (typeof window === 'undefined') return;
		const mql = window.matchMedia('(max-width: 767px)');
		let handle: { destroy: () => void } | null = null;

		const wire = () => {
			handle?.destroy();
			handle = null;
			if (!mql.matches) return;
			handle = edgeSwipe(window, {
				edge: 'left',
				edgeWidth: 24,
				threshold: 50,
				onCommit: () => window.dispatchEvent(new CustomEvent('sayano:open-sidebar')),
			});
		};

		wire();
		mql.addEventListener('change', wire);
		return () => {
			mql.removeEventListener('change', wire);
			handle?.destroy();
		};
	});
</script>

<svelte:head>
	<title>Sayano AI</title>
	<link rel="icon" href={favicon} />
</svelte:head>

<!-- Open WebUI sidebar row (`sidebar-new-chat-button` and the pinned menu
     items): a rounded-xl 32px row, 16px icon, 13px label, translucent active
     tint. In the collapsed rail only the 30px icon tile remains. -->
{#snippet navRow(Icon: typeof BlocksIcon, label: string, current: boolean, hint: string = '', dot: boolean = false)}
	<span class="flex size-4 shrink-0 items-center justify-center self-center">
		<Icon class="size-4" strokeWidth={1.5} aria-hidden="true" />
	</span>
	<span class="flex flex-1 translate-y-[0.5px] self-center group-data-[collapsible=icon]:hidden">
		<span class="self-center text-[0.8125rem] leading-5">{label}</span>
	</span>
	{#if dot}
		<span
			class="mr-1 inline-block size-1.5 shrink-0 self-center rounded-full bg-sky-500 group-data-[collapsible=icon]:hidden"
			aria-label="new scheduled run"
			title="A scheduled run finished since you last looked"
		></span>
	{/if}
	{#if hint}
		<span
			class="hover-reveal hidden items-center self-center text-xs text-gray-400 group-data-[collapsible=icon]:hidden! md:flex dark:text-gray-600"
			aria-hidden="true">{hint}</span
		>
	{/if}
	<span class="sr-only">{current ? '(current)' : ''}</span>
{/snippet}

{#snippet navLink(href: string, Icon: typeof BlocksIcon, label: string, current: boolean, dot: boolean = false)}
	<a
		{href}
		aria-current={current ? 'page' : undefined}
		aria-label={label}
		title={label}
		class="group/reveal flex grow items-center space-x-2 rounded-xl px-2 py-1.5 outline-none transition group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:grow-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:space-x-0 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0 {current
			? 'bg-black/[0.035] dark:bg-white/[0.045]'
			: 'hover:bg-gray-100 dark:hover:bg-gray-900'}"
	>
		{@render navRow(Icon, label, current, '', dot)}
	</a>
{/snippet}

{#if gate === 'auth-pending' || gate === 'me-pending'}
	<!-- Auth state or the /me round-trip still resolving (the latter incl. a
	     retry across a manager redeploy). A bare centered spinner avoids
	     flashing the gate or the app. -->
	<div class="flex h-svh items-center justify-center bg-background">
		<div
			class="size-5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-700 dark:border-gray-800 dark:border-t-gray-200"
			aria-label="Loading"
			role="status"
		></div>
	</div>
{:else if gate === 'login'}
	<!-- Login gate (auth.md "Login gate"): a single clean centered Sign-in
	     screen. The app `children` are never mounted until authenticated, so no
	     REST/WS call ever fires without a token. -->
	<div class="flex h-svh items-center justify-center bg-background px-6">
		<div
			class="flex w-full max-w-sm flex-col items-center gap-6 rounded-3xl border border-gray-100 bg-card px-8 py-12 text-center dark:border-gray-850"
		>
			<span class="flex items-center gap-2.5 text-lg font-medium tracking-[-0.01em] text-gray-900 dark:text-white">
				<span class="inline-block size-3 shrink-0 rounded-full bg-gray-900 dark:bg-white" aria-hidden="true"
				></span>
				Sayano AI
			</span>
			<p class="text-sm text-gray-500 dark:text-gray-400">
				Sign in to access your assistant, tools, and skills.
			</p>
			<button
				type="button"
				class="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-normal text-white transition hover:bg-black disabled:opacity-60 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
				onclick={handleSignIn}
				disabled={signingIn}
			>
				{signingIn ? 'Redirecting…' : 'Sign in'}
			</button>
		</div>
	</div>
{:else if gate === 'reconnect'}
	<!-- /me couldn't be reached (e.g. the manager is mid-redeploy). Show a
	     reconnect state — NEVER the onboarding wizard, which would re-trigger
	     onboarding for already-onboarded users — and auto-retry until it recovers. -->
	<div class="flex h-svh items-center justify-center bg-background px-6">
		<div
			class="flex w-full max-w-sm flex-col items-center gap-5 rounded-3xl border border-gray-100 bg-card px-8 py-12 text-center dark:border-gray-850"
		>
			<span class="flex items-center gap-2.5 text-lg font-medium tracking-[-0.01em] text-gray-900 dark:text-white">
				<span class="inline-block size-3 shrink-0 rounded-full bg-gray-900 dark:bg-white" aria-hidden="true"></span>
				Sayano AI
			</span>
			<p class="text-sm text-gray-500 dark:text-gray-400">
				Couldn’t reach the server — it may be redeploying. Retrying…
			</p>
			<button
				type="button"
				class="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-normal text-white transition hover:bg-black disabled:opacity-60 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
				onclick={() => void reloadMe()}
			>
				Retry now
			</button>
		</div>
	</div>
{:else if gate === 'onboarding'}
	<!-- Mandatory first-run gate (roadmap.md #4). The wizard renders INSTEAD of
	     the app shell — sidebar/composer/marketplace are unreachable until the
	     user finishes. setMe() in the wizard flips state.me.onboarded → true,
	     this branch re-evaluates, and the app shell mounts; no goto() needed. -->
	<OnboardingWizard />
{:else}
<!-- V2 app shell (SAY-1806), ported from the Open WebUI chat shell: a flat
     245px sidebar (gray-50 / gray-950) that collapses to a 42px icon rail,
     next to a plain page. Every control from the previous shell is still here —
     only the chrome changed. -->
<Sidebar.Provider style="--sidebar-width: 245px; --sidebar-width-mobile: 245px; --sidebar-width-icon: 42px;">
	<SidebarMobileOpener />
	<Sidebar.Root variant="sidebar" collapsible="icon">
		<Sidebar.Header
			class="sticky top-0 z-10 gap-0 px-1 pt-[max(env(safe-area-inset-top),0.375rem)] pb-1 text-gray-600 dark:text-gray-400"
		>
			<!-- Expanded: logo tile · product name · close. Collapsed rail: the
			     logo tile alone, which becomes the open-sidebar glyph on hover. -->
			<div class="flex justify-between space-x-1 group-data-[collapsible=icon]:hidden">
				<a
					href="/"
					class="flex size-8.5 items-center justify-center rounded-xl transition hover:bg-gray-100 dark:hover:bg-gray-900"
					aria-label="Go to start"
					title="Sayano AI"
				>
					<span
						class="flex size-5 items-center justify-center rounded-full bg-black text-white dark:bg-white dark:text-black"
						aria-hidden="true"
					>
						<span class="block size-1.5 rounded-full bg-current"></span>
					</span>
				</a>
				<a href="/" class="flex flex-1 px-0.5">
					<span class="self-center font-normal text-gray-700 dark:text-gray-200">Sayano AI</span>
				</a>
				<SidebarToggle variant="header" />
			</div>
			<div class="hidden pb-1 group-data-[collapsible=icon]:block">
				<SidebarToggle variant="rail" />
			</div>
		</Sidebar.Header>
		<Sidebar.Content class="scrollbar-hidden gap-0 space-y-1.5 px-1 pt-2.5 pb-2.5">
			<!-- Top nav block: New Chat, Search, then the pinned app areas. -->
			<nav class="flex flex-col pb-1" aria-label="Primary">
				<div class="flex justify-center text-gray-700 group-data-[collapsible=icon]:justify-center dark:text-gray-300">
					<a
						id="sidebar-new-chat-button"
						href="/"
						aria-label="New chat"
						title="New chat"
						class="group/reveal flex grow items-center space-x-2 rounded-xl px-2 py-1.5 outline-none transition hover:bg-gray-100 group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:grow-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:space-x-0 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0 dark:hover:bg-gray-900"
					>
						{@render navRow(SquarePenIcon, 'New chat', false, `${modKey}⇧O`)}
					</a>
				</div>
				<div class="flex justify-center text-gray-700 dark:text-gray-300">
					<button
						id="sidebar-search-button"
						type="button"
						aria-label="Search"
						title="Search"
						aria-haspopup="dialog"
						aria-expanded={searchOpen}
						class="group/reveal flex grow items-center space-x-2 rounded-xl px-2 py-1.5 text-left outline-none transition group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:grow-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:space-x-0 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0 {searchOpen
							? 'bg-black/[0.035] dark:bg-white/[0.045]'
							: 'hover:bg-gray-100 dark:hover:bg-gray-900'}"
						onclick={() => (searchOpen = true)}
					>
						{@render navRow(SearchIcon, 'Search', searchOpen, `${modKey}K`)}
					</button>
				</div>
				<div class="flex flex-col text-gray-700 dark:text-gray-300" id="pinned-menu-items-list">
					{@render navLink('/marketplace', BlocksIcon, 'Tools and skills',
						page.url.pathname.startsWith('/marketplace') &&
							!page.url.pathname.startsWith('/marketplace/admin'))}
					{@render navLink('/scheduled', ClockIcon, 'Scheduled',
						page.url.pathname.startsWith('/scheduled'), scheduledHasUnread())}
					{@render navLink('/reports', ChartColumnIcon, 'Reports',
						page.url.pathname.startsWith('/reports'))}
					{@render navLink('/testing-ground', FlaskConicalIcon, 'Testing Ground',
						page.url.pathname.startsWith('/testing-ground'))}
					{#if me()?.isAdmin}
						{@render navLink('/marketplace/admin', ShieldIcon, 'Admin',
							page.url.pathname.startsWith('/marketplace/admin'))}
					{/if}
				</div>
			</nav>
			<!-- ⌘K chat search: full-text over titles + messages with a preview pane. -->
			<SearchDialog bind:open={searchOpen} />
			<div class="flex min-h-0 flex-1 flex-col group-data-[collapsible=icon]:hidden">
				<!-- Folders (SAY-1820) sit above the date-grouped chats; a chat lives in
				     exactly one of the two. -->
				<FolderList {activeId} />
				<SessionList {activeId} />
				<!-- Chats others shared with me and the Trash keep their own
				     collapsible sections below the user's own conversations. -->
				<SharedWithMeList />
				<TrashList />
			</div>
		</Sidebar.Content>
		<!-- Bulk actions over the selected chats (SAY-1820); only while selecting. -->
		<div class="group-data-[collapsible=icon]:hidden">
			<BulkActionBar />
		</div>
		<Sidebar.Footer
			class="sticky bottom-0 z-10 gap-0 px-1 pt-1 pb-[max(env(safe-area-inset-bottom),0.375rem)] group-data-[collapsible=icon]:items-center"
		>
			<div class="flex flex-col gap-1 group-data-[collapsible=icon]:hidden">
				{#if me()?.isAdmin}
					<!-- Compact usage-limit meter (admin-only); links to the Providers widget. -->
					<SidebarUsage />
				{/if}
				<InstallPrompt />
			</div>
			<!-- Signed-in identity row → user menu (theme, reset onboarding, sign out). -->
			<SidebarUserMenu
				label={userLabel}
				isAdmin={Boolean(me()?.isAdmin)}
				onResetOnboarding={handleResetOnboarding}
				onSignOut={() => {
					// Clean PostHog identity on explicit sign-out so a
					// subsequent different login on the same browser doesn't
					// merge into the prior person. logout() redirects away.
					resetUser();
					logout();
				}}
			/>
		</Sidebar.Footer>
	</Sidebar.Root>
	<Sidebar.Inset class="min-w-0">
		<!-- overflow-hidden: scrolling belongs to MessageFeed and the side
		     panels, not <main>. min-w-0 on Sidebar.Inset: as a flex-row child of
		     Sidebar.Provider it defaults to min-width:auto, so a wide descendant
		     would grow it past the mobile viewport. -->
		<main class="h-svh overflow-hidden">
			{@render children()}
		</main>
	</Sidebar.Inset>
</Sidebar.Provider>
{/if}

{#if updated.current}
	<UpdateBanner />
{/if}

<Toaster richColors position="top-right" closeButton theme="system" />

<style>
	/* The combined preference is installed on <html>, making every state swap
	   instant even for utility-class transitions and animations. */
	:global([data-reduced-motion] *),
	:global([data-reduced-motion] *::before),
	:global([data-reduced-motion] *::after) {
		animation: none !important;
		transition: none !important;
	}

	/* View Transitions cross-fade for SvelteKit route navigation. Foundations
	   agent owns layout.css, so we keep this rule scoped here with :global()
	   selectors targeting the pseudo-elements the View Transitions API
	   creates on the document root. */
	:global(::view-transition-old(root)),
	:global(::view-transition-new(root)) {
		animation-duration: 200ms;
		animation-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
	}
</style>
